import React, { useState } from 'react';

const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'How quickly can I see a doctor?',
      answer: 'We guarantee you\'ll connect with a board-certified physician within 24 hours of your request. For urgent matters, many of our doctors are available for same-day consultations, and we often have availability within just a few hours.'
    },
    {
      question: 'What types of conditions can be treated virtually?',
      answer: 'Our virtual primary care covers a wide range of conditions including cold and flu symptoms, allergies, skin conditions, mental health concerns, chronic disease management (diabetes, hypertension), preventive care, prescription refills, and many acute illnesses. For conditions requiring physical examination or emergency care, we\'ll guide you to appropriate in-person care.'
    },
    {
      question: 'Are the doctors board-certified?',
      answer: 'Yes, all our physicians are board-certified in family medicine, internal medicine, or other relevant specialties. They are licensed to practice in your state and have extensive experience in both traditional and virtual care settings.'
    },
    {
      question: 'How do prescriptions work?',
      answer: 'When medically appropriate, your doctor can prescribe medications during your virtual visit. Prescriptions are sent electronically to your preferred pharmacy, and you\'ll receive a notification when they\'re ready for pickup. We can also help coordinate prescription deliveries when available.'
    },
    {
      question: 'Is my health information secure?',
      answer: 'Absolutely. We use bank-level encryption and are fully HIPAA compliant. All video calls are conducted through secure, encrypted platforms, and your health information is stored on secure servers with multiple layers of protection.'
    },
    {
      question: 'Can I use this with my existing insurance?',
      answer: 'Virtual Primary Care is a subscription service that works alongside your existing insurance. While the subscription fee isn\'t typically covered by insurance, any prescriptions, lab work, or specialist referrals we provide can often be processed through your regular insurance benefits.'
    },
    {
      question: 'What if I need to see a specialist?',
      answer: 'As part of your subscription, we provide referrals to trusted specialists in our network. Family and Premium plan members receive priority scheduling and discounted rates with our specialist partners.'
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel your subscription at any time. We offer a 30-day money-back guarantee for new subscribers. If you cancel, you\'ll continue to have access to your subscription benefits until the end of your current billing period.'
    },
    {
      question: 'Do you offer family plans?',
      answer: 'Yes! Our Family plan covers up to 4 family members and includes specialized pediatric care. Each family member gets their own secure portal and can schedule independent consultations with age-appropriate physicians.'
    },
    {
      question: 'What happens in case of a medical emergency?',
      answer: 'Virtual Primary Care is not intended for medical emergencies. If you\'re experiencing a life-threatening emergency, please call 911 immediately. For urgent but non-emergency situations, we can often provide same-day consultations to help determine the appropriate level of care needed.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600">
            Everything you need to know about Virtual Primary Care
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between"
              >
                <span className="font-semibold text-gray-900 pr-4">
                  {faq.question}
                </span>
                <i className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-teal-600 text-xl flex-shrink-0`}></i>
              </button>
              
              {openIndex === index && (
                <div className="px-6 pb-4 bg-gray-50">
                  <p className="text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Still Have Questions?
            </h3>
            <p className="text-gray-600 mb-6">
              Our patient support team is here to help you get started
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap">
                Contact Support
              </button>
              <button className="border-2 border-teal-600 text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap">
                Schedule a Demo
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;